import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import {toast} from 'react-toastify'
import GenericTable from '../components/shared/generic-table'
import {useAuthClient} from '../utils/use-auth-client'
import '../styles/control-center-styles.css'
import {ReactComponent as RefreshIcon} from '../assets/refresh-icon.svg'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const DEFAULT_TIMEZONE = 'America/Chicago'

const formatDate = dateStr => {
  if (!dateStr) return 'N/A'
  const d = dayjs(dateStr)
  return d.isValid()
    ? d.tz(DEFAULT_TIMEZONE).format('MM/DD/YYYY hh:mmA z')
    : 'Invalid date'
}

// ─── Debounce Hook ────────────────────────────────────────────────────────────

const useDebouncedValue = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputStyle = {
  padding: '10px 12px',
  borderRadius: '4px',
  border: '1px solid #d4d4d4',
  fontSize: '14px',
  fontFamily: 'Open Sans, sans-serif',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#ffffff',
  color: '#231f20',
  transition: 'border-color 0.2s',
}

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  marginBottom: '6px',
  fontWeight: '600',
  textTransform: 'uppercase',
  color: '#666',
  letterSpacing: '0.5px',
}

// ─── Order Details Modal ──────────────────────────────────────────────────────

const OrderDetailsModal = ({order, onClose}) => {
  if (!order) return null

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <section className="appointment-info-modal">
        <div className="appointment-modal-header">
          <h2>Order Details</h2>
          <button onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="appointment-info-content">
          <div className="appointment-details-container">
            <div className="appointment-details-grid">
              <div className="appointment-detail">
                <span className="detail-label">Order ID</span>
                <span className="detail-value">{order.order_id}</span>
              </div>
              <div className="appointment-detail">
                <span className="detail-label">Order Completed</span>
                <span className="detail-value">
                  {formatDate(order.order_completed_date)}
                </span>
              </div>
              <div className="appointment-detail">
                <span className="detail-label">Client Email</span>
                <span className="detail-value">
                  {order.client_email || 'N/A'}
                </span>
              </div>
              <div className="appointment-detail">
                <span className="detail-label">Thryv ID</span>
                <span className="detail-value">{order.thryv_id || 'N/A'}</span>
              </div>
              <div className="appointment-detail">
                <span className="detail-label">Company Name</span>
                <span className="detail-value">
                  {order.company_name || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

const COUNT_PER_PAGE = 15

function CompletedLinks() {
  const client = useAuthClient()

  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const dateRangeRef = useRef(null)

  const [filters, setFilters] = useState({
    companyName: '',
    clientEmail: '',
    thryvId: '',
    startDate: '',
    endDate: '',
  })

  const debouncedCompanyName = useDebouncedValue(filters.companyName)
  const debouncedClientEmail = useDebouncedValue(filters.clientEmail)
  const debouncedThryvId = useDebouncedValue(filters.thryvId)
  const debouncedStartDate = useDebouncedValue(filters.startDate)
  const debouncedEndDate = useDebouncedValue(filters.endDate)

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value}
      if (key === 'startDate' && prev.endDate && value > prev.endDate) {
        updated.endDate = ''
      }
      return updated
    })
  }

  const clearFilters = () => {
    setFilters({
      companyName: '',
      clientEmail: '',
      thryvId: '',
      startDate: '',
      endDate: '',
    })
    setIsDateRangeOpen(false)
    setPage(1)
  }

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        isDateRangeOpen &&
        dateRangeRef.current &&
        !dateRangeRef.current.contains(e.target)
      ) {
        setIsDateRangeOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDateRangeOpen])

  useEffect(() => {
    setPage(1)
  }, [
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  const fetchCompletedLinks = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: COUNT_PER_PAGE.toString(),
      })
      if (debouncedCompanyName)
        params.append('companyName', debouncedCompanyName)
      if (debouncedClientEmail)
        params.append('clientEmail', debouncedClientEmail)
      if (debouncedThryvId) params.append('thryvId', debouncedThryvId)
      if (debouncedStartDate) params.append('startDate', debouncedStartDate)
      if (debouncedEndDate) params.append('endDate', debouncedEndDate)

      const data = await client(`cc/order/completed-links?${params}`)

      if (data.success && data.response) {
        setRows(data.response.data || [])
        if (data.response.pagination) {
          setPagination({
            page: data.response.pagination.page,
            totalPages: data.response.pagination.totalPages,
            total: data.response.pagination.total,
            hasNext: data.response.pagination.hasNext,
            hasPrev: data.response.pagination.hasPrev,
          })
        }
      }
    } catch (err) {
      console.error('Error fetching completed links:', err)
      toast.error('Error fetching completed links')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [
    client,
    page,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  useEffect(() => {
    fetchCompletedLinks()
  }, [fetchCompletedLinks])

  const handleDateRangeChange = value => {
    if (Array.isArray(value)) {
      const [start, end] = value
      updateFilter('startDate', start ? dayjs(start).format('YYYY-MM-DD') : '')
      updateFilter('endDate', end ? dayjs(end).format('YYYY-MM-DD') : '')
      if (start && end) setIsDateRangeOpen(false)
    }
  }

  const getCalendarValue = () => {
    if (!filters.startDate && !filters.endDate) return null
    const toLocal = str => {
      if (!str) return null
      const [y, m, d] = str.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    return [toLocal(filters.startDate), toLocal(filters.endDate)]
  }

  const dateRangeLabel = filters.startDate
    ? filters.endDate
      ? `${dayjs(filters.startDate).format('MM/DD/YYYY')} - ${dayjs(
          filters.endDate,
        ).format('MM/DD/YYYY')}`
      : `${dayjs(filters.startDate).format('MM/DD/YYYY')} - Select end date`
    : ''

  const columns = useMemo(
    () => [
      {key: 'order_id', label: 'Order ID'},
      {
        key: 'order_completed_date',
        label: 'Completed Date',
        render: (_, row) => (
          <p
            className="formmated-date"
            style={{margin: 0, fontSize: '12px', whiteSpace: 'nowrap'}}
          >
            {formatDate(row.order_completed_date)}
          </p>
        ),
      },
      {key: 'client_email', label: 'Client Email'},
      {key: 'thryv_id', label: 'Thryv ID'},
      {key: 'company_name', label: 'Company Name'},
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => (
          <button
            className="table-button"
            onClick={e => {
              e.stopPropagation()
              setSelectedOrder(row)
            }}
          >
            Details
          </button>
        ),
      },
    ],
    [],
  )

  const displayStart = pagination.total
    ? (pagination.page - 1) * COUNT_PER_PAGE + 1
    : 0
  const displayEnd = Math.min(
    pagination.page * COUNT_PER_PAGE,
    pagination.total,
  )

  return (
    <div style={{padding: '24px'}}>
      <h3 className="control-center-item-header" style={{marginBottom: '8px'}}>
        Completed Purchase Links
      </h3>

      {pagination.total > 0 && (
        <h4
          className="control-center-display-text"
          style={{marginBottom: '16px'}}
        >
          Displaying {displayStart}-{displayEnd} out of {pagination.total}
        </h4>
      )}

      {/* Filters */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          border: '1px solid #e9e9e9',
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <div style={{flex: '1 1 180px', minWidth: '160px'}}>
          <label style={labelStyle}>Company Name</label>
          <input
            type="text"
            placeholder="Filter by company"
            value={filters.companyName}
            onChange={e => updateFilter('companyName', e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#fa6000')}
            onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
          />
        </div>

        <div style={{flex: '1 1 200px', minWidth: '180px'}}>
          <label style={labelStyle}>Client Email</label>
          <input
            type="text"
            placeholder="Filter by email"
            value={filters.clientEmail}
            onChange={e => updateFilter('clientEmail', e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#fa6000')}
            onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
          />
        </div>

        <div style={{flex: '1 1 160px', minWidth: '140px'}}>
          <label style={labelStyle}>Thryv ID</label>
          <input
            type="text"
            placeholder="Filter by Thryv ID"
            value={filters.thryvId}
            onChange={e => updateFilter('thryvId', e.target.value)}
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#fa6000')}
            onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
          />
        </div>

        <div style={{flex: '1 1 260px', minWidth: '220px'}} ref={dateRangeRef}>
          <label style={labelStyle}>Date Range</label>
          <div style={{position: 'relative'}}>
            <input
              type="text"
              readOnly
              value={dateRangeLabel}
              placeholder="Select date range"
              onClick={() => setIsDateRangeOpen(prev => !prev)}
              style={{...inputStyle, cursor: 'pointer'}}
              onFocus={e => (e.target.style.borderColor = '#fa6000')}
              onBlur={e => (e.target.style.borderColor = '#d4d4d4')}
            />
            {isDateRangeOpen && (
              <div
                style={{
                  position: 'absolute',
                  zIndex: 20,
                  marginTop: '8px',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <Calendar
                  selectRange
                  onChange={handleDateRangeChange}
                  value={getCalendarValue()}
                />
              </div>
            )}
          </div>
        </div>

        <div style={{flex: '0 0 auto', display: 'flex', gap: '8px'}}>
          <label style={{...labelStyle, color: 'transparent'}}>&nbsp;</label>
          <button
            onClick={clearFilters}
            style={{
              padding: '10px 16px',
              borderRadius: '4px',
              border: '1px solid #d4d4d4',
              fontSize: '14px',
              fontFamily: 'Open Sans, sans-serif',
              backgroundColor: '#ffffff',
              color: '#fa6000',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#fa6000'
              e.currentTarget.style.color = '#ffffff'
              e.currentTarget.style.borderColor = '#fa6000'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff'
              e.currentTarget.style.color = '#fa6000'
              e.currentTarget.style.borderColor = '#d4d4d4'
            }}
          >
            Clear Filters
          </button>
          <button
            onClick={fetchCompletedLinks}
            disabled={isLoading}
            className="refresh-button"
            style={{
              padding: '10px 16px',
              borderRadius: '4px',
              border: '1px solid #d4d4d4',
              fontSize: '14px',
              fontFamily: 'Open Sans, sans-serif',
              backgroundColor: '#ffffff',
              color: isLoading ? '#aaa' : '#fa6000',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshIcon
              width="14"
              height="14"
              style={{
                animation: isLoading ? 'spin 0.8s linear infinite' : 'none',
              }}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div
          style={{
            textAlign: 'center',
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        >
          <p style={{color: '#808080', fontSize: '14px'}}>Loading...</p>
        </div>
      ) : rows.length > 0 ? (
        <div className="control-center-table-wrapper">
          <GenericTable
            columns={columns}
            rows={rows}
            emptyMessage="No completed links found."
            pagination={{
              page: pagination.page,
              totalPages: pagination.totalPages,
              hasPrev: pagination.hasPrev,
              hasNext: pagination.hasNext,
              onPageChange: setPage,
            }}
          />
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        >
          <h4 style={{color: '#808080', fontSize: '16px', fontWeight: '600'}}>
            No Completed Purchase Links found
          </h4>
          <p style={{color: '#999', fontSize: '14px', marginTop: '8px'}}>
            Try adjusting your filters to see more results
          </p>
        </div>
      )}

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  )
}

export {CompletedLinks}
